/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { useLocation, useNavigate } from 'react-router-dom'
import '../../../assets/styles/custom-styles.css'
import useNotification from '../../../hooks/useNotification'
import useLoader from '../../../hooks/useLoader'
import useForm from '../../../hooks/useForm'
import Form from '../../../components/forms/Form'
import DocumentTypeEnums from '../../../enums/document-enum'
import {
  cleanPayload,
  setDefaultFieldValues,
  getMissingRequiredFields,
  convertStringsToFloats,
  normalizeFormErrors,
} from '../../../utils/objectUtils'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { transactionFields } from '../../../constants/forms/almoxarifado/transactionFields'
import ItemListSection from '../../../components/forms/ItemListSection'
import { v4 as uuidv4 } from 'uuid'
import { useTransactionPageLogic } from './hooks/useTransactionPageLogic'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { Box } from '@mui/material'
import { forEach } from 'lodash'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import TransactionEnums from '../../../enums/transaction-enum'
import { useOrgan } from '../../../hooks/useOrgan'

const CreateTransactionPage = () => {
  const location = useLocation()
  const { selectedOrgan } = useOrgan()

  const queryParams = new URLSearchParams(location.search)
  const type = queryParams.get('type')

  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()
  const queryClient = useQueryClient()
  const [showEquipmentsSection, setShowEquipmentsSection] = useState(false)
  const [showProductsSection, setShowProductsSection] = useState(false)

  const [transactionSection, productsSection, equipmentsSection] =
    transactionFields

  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues([transactionSection]),
    [transactionSection]
  )

  useEffect(() => {
    if (!type) return

    const transactionTypeMap = {
      entrada: { 
        value: TransactionEnums.ENTRADA, 
        label: 'ENTRADA' },
      transferencia: {
        value: TransactionEnums.TRANSFERENCIA,
        label: 'TRANSFERÊNCIA',
      },
      'saida-interna': { 
        value: TransactionEnums.SAIDA, 
        label: 'SAIDA' },
    }

    const transactionType = transactionTypeMap[type.toLowerCase()]

    if (transactionType) {
      setFormData((prev) => ({
        ...prev,
        transaction: {
          ...prev.transaction,
          transaction_type_id: transactionType,
        },
      }))
    }
  }, [type])

  const {
    formData: itemFormData,
    handleChange: handleItemChange,
    setFormData: setItemFormData,
    resetForm: resetItemForm,
  } = useForm(setDefaultFieldValues([productsSection]), [productsSection])
  const {
    formData: equipmentFormData,
    handleChange: handleEquipmentChange,
    setFormData: setEquipmentFormData,
    resetForm: resetEquipmentForm,
  } = useForm(setDefaultFieldValues([equipmentsSection]), [equipmentsSection])

  const [items, setItems] = useState([])
  const [equipments, setEquipments] = useState([])

  const handleItemsChange = useCallback(
    (newItems) => {
      setItems(newItems)
      setFormData((prev) => ({ ...prev, items: newItems }))
    },
    [setFormData]
  )

  const handleEquipmentsChange = useCallback(
    (newEquipments) => {
      setEquipments(newEquipments)
      setFormData((prev) => ({ ...prev, equipments: newEquipments }))
    },
    [setFormData]
  )

  // --- Hooks e buscas de dados no NÍVEL SUPERIOR ---
  const transactionTypeSelect = useRemoteSelectOptions({
    queryKey: ['almox_transactions_types'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.transactionTypes.get,
        params
      )
      return response.result
    },
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: 'transaction.transaction_type_id',
    formData,
    setFormData,
  })

  const isEntrada =
    transactionTypeSelect.selectedValue?.value === TransactionEnums.ENTRADA ||
    formData.transaction?.transaction_type_id === TransactionEnums.ENTRADA
  const isTransferencia =
    transactionTypeSelect.selectedValue?.value ===
      TransactionEnums.TRANSFERENCIA ||
    formData.transaction?.transaction_type_id === TransactionEnums.TRANSFERENCIA
  const isSaidaInterna =
    transactionTypeSelect.selectedValue?.value === TransactionEnums.SAIDA ||
    formData.transaction?.transaction_type_id === TransactionEnums.SAIDA

  const supplierSelect = useRemoteSelectOptions({
    queryKey: ['almox_suppliers'],
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    baseServiceGet,
    entityPath: entities.suppliers.get,
    columnDataBase: ['id', 'alias', 'name'],
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias} - ${item.name}`,
    }),
    fieldId: 'transaction.supplier_id',
    formData,
    setFormData,
    refetchOnWindowFocus: true,
  })

  const docTypeSelect = useRemoteSelectOptions({
    queryKey: ['almox_documents_types'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.documentsTypes.get, params)
      return response.result
    },
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: 'transaction.doc_type_id',
    formData,
    setFormData,
  })

  const { data: organizations } = useQueryWithLoader({
    queryKey: ['almox_organizations'],
    queryFn: async () => {
      const response = await baseServiceGet(entities.organizations.get, {
        deleted_at: false,
      })
      return response.result.data
    },
  })

  const senderSelect = useSelectOptions({
    fieldId: 'transaction.sender_id',
    formData,
    rawOptions: [TransactionEnums.TRANSFERENCIA].includes(
      transactionTypeSelect.selectedValue?.value
    )
      ? organizations?.filter((org) => org.id === selectedOrgan?.id)
      : [],
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
    setFormData,
  })

  const senderIdRef = useRef(senderSelect.selectedValue?.value)

  const senderAddressSelect = useRemoteSelectOptions({
    queryKey: ['sender_address', senderSelect.selectedValue?.value],
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    baseServiceGet,
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    entityPath: entities.addresses.get,
    extraFilters: {
      addressable_type: 'organization',
      addressable_id: senderSelect.selectedValue?.value,
    },
    defaultSort: {
      column: 'id',
      order: 'desc',
    },
    enabled: !!senderSelect.selectedValue?.value,
    fieldId: 'transaction.sender_address_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
    refetchWhen: () => {
      if (senderIdRef.current !== senderSelect.selectedValue?.value) {
        senderIdRef.current = senderSelect.selectedValue?.value
        senderAddressSelect.refetch({
          cleanedForm: true,
        })
        return true
      }
      return false
    },
  })

  const receiverSelect = useSelectOptions({
    fieldId: 'transaction.receiver_id',
    formData,
    rawOptions: organizations,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
    setFormData,
  })

  const receiverIdRef = useRef(receiverSelect.selectedValue?.value)

  const receiverAddressSelect = useRemoteSelectOptions({
    queryKey: [
      'receiver_address',
      isEntrada ? selectedOrgan?.id : receiverSelect.selectedValue?.value,
    ],
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    baseServiceGet,
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    entityPath: entities.addresses.get,
    extraFilters: {
      // ENTRADA → endereços do comprador (organização logada)
      ...(isEntrada && {
        addressable_type: 'organization',
        addressable_id: selectedOrgan?.id,
      }),
      // TRANSFERÊNCIA → endereços do estoque de destino
      ...(isTransferencia && {
        addressable_type: 'organization',
        addressable_id: receiverSelect.selectedValue?.value,
        id: { $ne: senderAddressSelect.selectedValue?.value },
      }),
    },
    defaultSort: {
      column: 'id',
      order: 'desc',
    },
    enabled:
      (isEntrada && !!selectedOrgan?.id) ||
      (isTransferencia && !!receiverSelect.selectedValue?.value),
    fieldId: 'transaction.receiver_address_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
    refetchWhen: () => {
      if (isTransferencia) {
        if (receiverIdRef.current !== receiverSelect.selectedValue?.value) {
          receiverIdRef.current = receiverSelect.selectedValue?.value
          receiverAddressSelect.refetch({ cleanedForm: true })
          return true
        }
      }
      return false
    },
  })

  const customerSelect = useRemoteSelectOptions({
    queryKey: ['almox_customers'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        alias: ['ALUCOM', 'MOREIA', 'IP', 'AS SISTEMAS'],
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.customers.get, params)
      return response.result
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    fieldId: 'transaction.customer_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const customerSelectedRef = useRef(customerSelect.selectedValue?.value)
  const receiverSelectedRef = useRef(receiverSelect.selectedValue?.value)

  // Quando for entrada eu busco os endereço do comprador (buyer.id)
  const addressesSelect = useRemoteSelectOptions({
    queryKey: ['almox_addresses'],
    baseServiceGet,
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    entityPath: entities.addresses.get,
    extraFilters: {
      // ENTRADA → endereços do comprador (organização logada)
      ...(isEntrada && {
        addressable_type: 'organization',
        addressable_id: selectedOrgan?.id, // ⬅️ Corrigido: usa diretamente o ID da organização logada
      }),

      // TRANSFERÊNCIA - endereços do estoque de recebimento
      ...(isTransferencia && {
        addressable_type: 'organization',
        addressable_id: organizations?.find(
          (receiver) => receiver.id === receiverSelect.selectedValue?.value
        )?.id,

        id: { $ne: senderAddressSelect.selectedValue?.value },
      }),
    },
    fieldId: 'items.address_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    enabled:
      (isEntrada && selectedOrgan?.id) ||
      (isTransferencia && receiverSelect.selectedValue?.value),
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
    refetchWhen: () => {
      if (customerSelectedRef.current !== customerSelect.selectedValue?.value) {
        customerSelectedRef.current = customerSelect.selectedValue?.value
        return true
      }
      if (receiverSelectedRef.current !== receiverSelect.selectedValue?.value) {
        receiverSelectedRef.current = receiverSelect.selectedValue?.value
        return true
      }
      return false
    },
  })

  const locationsSelect = useRemoteSelectOptions({
    queryKey: ['almox_locations', addressesSelect.selectedValue?.value],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { area: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.addresses.locations.get(addressesSelect.selectedValue?.value),
        params
      )
      return response.result
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_location}`,
    }),
    fieldId: 'items.location_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    refetchOnWindowFocus: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!addressesSelect.selectedValue?.value,
  })

  const productsSelect = useRemoteSelectOptions({
    queryKey: ['almox_products'],
    baseServiceGet,
    entityPath: entities.products.get,
    columnDataBase: ['name'],
    extraFilters: {
      $or: [
        { 'items.organization_id': { $eq: selectedOrgan?.id } },
        { $doesnthave: 'items' },
      ],
    },
    defaultSort: { column: 'is_asset', order: 'desc' },
    fieldId: 'items.product_id',
    formData: itemFormData,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.is_asset ? 'Equipamento' : 'Insumo'} - ${item.name}`,
    }),
    setFormData: setItemFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const productsItemsSelect = useRemoteSelectOptions({
    queryKey: [
      'almox_products_items',
      isTransferencia
        ? (senderSelect.selectedValue?.value ?? 'none')
        : (selectedOrgan?.id ?? 'none'),
      isTransferencia
        ? (senderAddressSelect.selectedValue?.value ?? 'none')
        : (itemFormData.items.address_id?.value ?? 'none'),
      itemFormData.items.category_id?.value ?? 'all',
      itemFormData.items.condition_id?.value ?? 'all',
    ],
    baseServiceGet,
    columnDataBase: ['code', 'product.name'],
    entityPath: entities.productsItems.get,
    extraFilters: {
      ...(isTransferencia
        ? {
            ...(itemFormData.items.category_id?.value && {
              category_id: { $eq: itemFormData.items.category_id.value },
            }),
            ...(itemFormData.items.condition_id?.value && {
              condition_id: { $eq: itemFormData.items.condition_id.value },
            }),
            address_id: { $eq: senderAddressSelect.selectedValue?.value },
            organization_id: { $eq: senderSelect.selectedValue?.value },
            'product.is_asset': { $eq: 0 },
          }
        : {
            organization_id: { $eq: selectedOrgan?.id },
          }),
      quantity: { $gt: 0 },
    },
    fieldId: 'items.product_item_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.product?.name ?? '-'} - Qtd: ${item.quantity ?? 0}`,
    }),
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: false,
    blockedIds: items.map((i) => i.product_item_id?.value),
    enabled: isTransferencia
      ? !!(
          senderSelect.selectedValue?.value &&
          senderAddressSelect.selectedValue?.value
        )
      : !!selectedOrgan?.id,
  })

  const categoriesSelect = useRemoteSelectOptions({
    queryKey: ['almox_categories'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }
      const response = await baseServiceGet(entities.categories.get, params)
      return response.result
    },
    fieldId: 'items.category_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
  })

  const conditionsSelect = useRemoteSelectOptions({
    queryKey: ['almox_conditions'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(entities.conditions.get, params)
      return response.result
    },
    fieldId: 'items.condition_id',
    formData: itemFormData,
    setFormData: setItemFormData,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
  })

  // Seta o campo category_id, condition_id e address_id de acordo com o product_item_id selecionado
  useEffect(() => {
    const productItem = productsItemsSelect.rawData?.find(
      (p) => p.id === Number(itemFormData.items.product_item_id?.value)
    )

    if (!productItem) return

    setItemFormData((prev) => ({
      ...prev,
      items: {
        ...prev.items,
        category_id: {
          value: productItem.category.id,
          label: productItem.category.name,
        },
        condition_id: {
          value: productItem.condition.id,
          label: productItem.condition.name,
        },
      },
    }))
  }, [itemFormData.items.product_item_id?.value, productsItemsSelect.rawData])

  const equipmentsSelect = useRemoteSelectOptions({
    queryKey: ['almox_equipments', senderSelect.selectedValue?.value],
    entityPath: entities.organizations.equipments.get(
      senderSelect.selectedValue?.value
    ),
    columnDataBase: ['number', 'name'],
    baseServiceGet,
    extraFilters: {
      status_id: { $eq: 1 },
      is_completed: { $eq: 1 },
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
    }),
    fieldId: 'equipments.equipment_id',
    formData: equipmentFormData,
    setFormData: setEquipmentFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
    enabled: !!senderSelect.selectedValue?.value,
    blockedIds: equipments.map((eq) => eq.equipment_id?.value),
  })

  const equipmentAddressesSelect = useRemoteSelectOptions({
    queryKey: ['almox_addresses_for_equipment'],
    baseServiceGet,
    columnDataBase: ['alias', 'zip', 'street', 'district'],
    extraFilters: {
      ...(isTransferencia && {
        addressable_type: 'organization',
        addressable_id: organizations?.find(
          (receiver) => receiver.id === receiverSelect.selectedValue?.value
        )?.id,

        id: { $ne: senderAddressSelect.selectedValue?.value },
      }),
    },
    entityPath: entities.addresses.get,
    fieldId: 'equipments.address_id',
    formData: equipmentFormData,
    setFormData: setEquipmentFormData,
    enabled: isTransferencia && receiverSelect.selectedValue?.value,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
    refetchWhen: () => {
      if (customerSelectedRef.current !== customerSelect.selectedValue?.value) {
        customerSelectedRef.current = customerSelect.selectedValue?.value
        return true
      }
      if (receiverSelectedRef.current !== receiverSelect.selectedValue?.value) {
        receiverSelectedRef.current = receiverSelect.selectedValue?.value
        return true
      }
      return false
    },
  })

  const equipmentLocationsSelect = useRemoteSelectOptions({
    queryKey: [
      'almox_equipment_locations',
      equipmentAddressesSelect.selectedValue?.value,
    ],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { area: search }),
      })

      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.addresses.locations.get(
          equipmentAddressesSelect.selectedValue?.value
        ),
        params
      )
      return response.result
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_location}`,
    }),
    fieldId: 'equipments.location_id',
    formData: equipmentFormData,
    setFormData: setEquipmentFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!equipmentAddressesSelect.selectedValue?.value,
  })

  const assetTypeOptionsRaw = useMemo(
    () => [
      { value: 'equipamento', name: 'Equipamento' },
      { value: 'insumo', name: 'Insumo' },
    ],
    []
  )

  const assetTypeSelect = useSelectOptions({
    fieldId: 'transaction.asset_type',
    formData,
    rawOptions: assetTypeOptionsRaw,
    isMulti: true,
    mapToOption: (item) => ({ value: item.value, label: item.name }),
    setFormData,
  })

  const {
    transactionFieldsState,
    productsFieldsState,
    equipmentsFieldsState,
    isDisabledButton,
    setIsEquipmentSelected,
    setIsInsumoSelected,
    setTransactionTypeSelectedValue,
    setReceiverSelectedValue,
    setSenderSelectedValue,
    setIsCustomerSelected,
    setIsBuyerSelected,
  } = useTransactionPageLogic(
    items,
    equipments,
    isEntrada,
    isTransferencia,
    productsSelect.rawData,
    productsSelect,
    setFormData,
    handleItemChange,
    addressesSelect,
    locationsSelect,
    equipmentAddressesSelect,
    equipmentLocationsSelect,
    false,
    docTypeSelect
  )

  const assetTypeSelectedValue = assetTypeSelect.selectedValue

  // Atualiza os valores de sender e receiver e tipo de transação
  useEffect(() => {
    setTransactionTypeSelectedValue(
      transactionTypeSelect.selectedValue?.value ?? null
    )
    setSenderSelectedValue(senderSelect.selectedValue?.value ?? null)
    setReceiverSelectedValue(receiverSelect.selectedValue?.value ?? null)
    setIsCustomerSelected(customerSelect.selectedValue?.value ?? null)
    setIsBuyerSelected(selectedOrgan?.id ?? null)
  }, [
    transactionTypeSelect.selectedValue?.value,
    senderSelect.selectedValue?.value,
    receiverSelect.selectedValue?.value,
    customerSelect.selectedValue?.value,
    selectedOrgan?.id,
    setTransactionTypeSelectedValue,
    setSenderSelectedValue,
    setReceiverSelectedValue,
    setIsCustomerSelected,
    setIsBuyerSelected,
  ])

  // Preenche endereço do item automaticamente quando for transferência
  useEffect(() => {
    if (isTransferencia && formData.transaction?.receiver_address_id?.value) {
      setItemFormData((prev) => ({
        ...prev,
        items: {
          ...prev.items,
          address_id: {
            value: formData.transaction.receiver_address_id.value,
            label: formData.transaction.receiver_address_id.label,
          },
        },
      }))
    }
  }, [isTransferencia, formData.transaction?.receiver_address_id?.value])

  // ⬅️ NOVO: Preenche endereço do item automaticamente quando for entrada
  useEffect(() => {
    if (isEntrada && !itemFormData.items?.address_id) {
      const defaultAddress =
        formData.transaction?.receiver_address_id || // ⬅️ Prioriza o endereço de destino da transação
        addressesSelect.selectedValue ||
        addressesSelect.options?.[0]
      if (defaultAddress) {
        setItemFormData((prev) => ({
          ...prev,
          items: {
            ...prev.items,
            address_id: {
              value: defaultAddress.value,
              label: defaultAddress.label,
            },
          },
        }))
      }
    }
  }, [
    isEntrada,
    formData.transaction?.receiver_address_id, // ⬅️ Dependência nova
    addressesSelect.selectedValue,
    addressesSelect.options,
    itemFormData.items?.address_id,
    setItemFormData,
  ])

  // Atualiza a visualização de equipamentos e insumos
  useEffect(() => {
    let hasEquipmento = false
    let hasInsumo = false

    if (isTransferencia && assetTypeSelectedValue) {
      hasEquipmento = assetTypeSelectedValue.some(
        (item) => item?.value === 'equipamento'
      )
      hasInsumo = assetTypeSelectedValue.some(
        (item) => item?.value === 'insumo'
      )
      setIsEquipmentSelected(hasEquipmento)
      setIsInsumoSelected(hasInsumo)
    }

    if (isEntrada) {
      hasInsumo = true
    }

    if (isTransferencia) {
      hasEquipmento = true
      hasInsumo = true
    }

    const showEquipmento = isTransferencia && hasEquipmento
    const showInsumo = hasInsumo

    setShowEquipmentsSection(showEquipmento)
    setShowProductsSection(showInsumo)
  }, [assetTypeSelectedValue, transactionTypeSelect.selectedValue?.value])

  const selects = {
    'transaction.transaction_type_id': transactionTypeSelect,
    'transaction.supplier_id': supplierSelect,
    'transaction.doc_type_id': docTypeSelect,
    'transaction.sender_id': senderSelect,
    'transaction.sender_address_id': senderAddressSelect,
    'transaction.receiver_id': receiverSelect,
    'transaction.receiver_address_id': receiverAddressSelect,
    'transaction.asset_type': assetTypeSelect,
    'transaction.customer_id': customerSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects]
  )
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects]
  )

  const itemSelects = {
    'items.product_id': productsSelect,
    'items.product_item_id': productsItemsSelect,
    'items.category_id': categoriesSelect,
    'items.condition_id': conditionsSelect,
    'items.address_id': addressesSelect,
    'items.location_id': locationsSelect,
  }

  const getSelectMetaItem = useCallback(
    (fieldId) => {
      const selectConfig = itemSelects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [itemSelects]
  )

  const getItemOptions = useCallback(
    (fieldId) => itemSelects[fieldId]?.options || [],
    [itemSelects]
  )
  const getItemSelectedValue = useCallback(
    (fieldId) => itemSelects[fieldId]?.selectedValue || null,
    [itemSelects]
  )

  const equipmentSelects = {
    'equipments.equipment_id': equipmentsSelect,
    'equipments.address_id': equipmentAddressesSelect,
    'equipments.location_id': equipmentLocationsSelect,
  }

  const getSelectMetaEquipment = useCallback(
    (fieldId) => {
      const selectConfig = equipmentSelects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [equipmentSelects]
  )

  const getEquipmentItemOptions = useCallback(
    (fieldId) => equipmentSelects[fieldId]?.options || [],
    [equipmentSelects]
  )
  const getEquipmentItemSelectedValue = useCallback(
    (fieldId) => equipmentSelects[fieldId]?.selectedValue || null,
    [equipmentSelects]
  )

  const handleSaveItem = useCallback(
    (itemToEdit) => {
      let updatedItems
      let itemData = {
        ...convertStringsToFloats(itemFormData.items),
        warranty_date: itemFormData.items.warranty_date,
      }

      // ⬅️ NOVO: Auto-preenche address_id se estiver vazio (entrada)
      if (isEntrada && !itemData.address_id) {
        const defaultAddress =
          addressesSelect.selectedValue || addressesSelect.options?.[0]
        if (defaultAddress) {
          itemData.address_id = {
            value: defaultAddress.value,
            label: defaultAddress.label,
          }
        }
      }

      const quantity = itemData.quantity
      if (quantity === null || quantity === undefined || quantity < 1) {
        showNotification('warning', 'O campo Quantidade deve ser maior que 0.')
        return false
      }

      const ignoreMap = {
        entrada: ['items.product_item_id'],
        transferencia: [
          'items.product_id',
          'items.warranty_date',
          'items.unit_cost',
        ],
      }

      const missingFields = getMissingRequiredFields({
        itemData,
        fields: productsSection.fields,
        prefix: 'items',
        ignore: isEntrada
          ? ignoreMap.entrada
          : isTransferencia
            ? ignoreMap.transferencia
            : [],
      })

      if (missingFields.length > 0) {
        showNotification('warning', 'Preencha todos os campos obrigatórios')
        return false
      }

      if (itemToEdit) {
        updatedItems = items.map((item) =>
          item.id === itemToEdit.id ? { ...item, ...itemData } : item
        )
      } else {
        updatedItems = [...items, { id: uuidv4().slice(0, 8), ...itemData }]
      }

      setItems(updatedItems)
      handleItemsChange(updatedItems)
      return true
    },
    [
      items,
      itemFormData,
      handleItemsChange,
      isEntrada,
      addressesSelect.selectedValue,
      addressesSelect.options,
      productsSection.fields,
    ]
  )

  const initializeItemForm = useCallback(
    (itemData) => {
      setItemFormData({
        items: {
          ...(itemData || {}),
          // Para transferência, usa o endereço de destino da transação
          ...(isTransferencia && formData.transaction?.receiver_address_id
            ? { address_id: formData.transaction.receiver_address_id }
            : {}),
          // Para entrada, usa o endereço de destino da transação
          ...(isEntrada && !itemData?.address_id
            ? {
                address_id:
                  formData.transaction?.receiver_address_id ||
                  addressesSelect.selectedValue ||
                  addressesSelect.options?.[0] ||
                  null,
              }
            : {}),
        },
      })
    },
    [
      setItemFormData,
      isEntrada,
      isTransferencia,
      formData.transaction?.receiver_address_id,
      addressesSelect.selectedValue,
      addressesSelect.options,
    ]
  )

  const handleSaveEquipment = useCallback(
    (equipmentToEdit) => {
      let updatedEquipments
      const equipmentData = convertStringsToFloats(equipmentFormData.equipments)

      const missingFields = getMissingRequiredFields({
        itemData: equipmentData,
        fields: equipmentsSection.fields,
        prefix: 'equipments',
        ignore: [],
      })

      if (missingFields.length > 0) {
        showNotification(
          'warning',
          'Preencha todos os campos obrigatórios para equipamento'
        )
        return false
      }

      if (equipmentToEdit) {
        updatedEquipments = equipments.map((equipment) =>
          equipment.id === equipmentToEdit.id
            ? { ...equipment, ...equipmentData }
            : equipment
        )
      } else {
        updatedEquipments = [
          ...equipments,
          { id: uuidv4().slice(0, 8), ...equipmentData },
        ]
      }

      setEquipments(updatedEquipments)
      handleEquipmentsChange(updatedEquipments)
      return true
    },
    [equipments, equipmentFormData, handleEquipmentsChange]
  )
  const calculateItemsTotal = (items) => {
    return items.reduce((total, item) => {
      const unitCost = parseFloat(item.unit_cost) || 0
      const quantity = parseInt(item.quantity) || 0
      return total + unitCost * quantity
    }, 0)
  }

  const initializeEquipmentForm = useCallback(
    (equipmentData) => {
      setEquipmentFormData({ equipments: equipmentData || {} })
    },
    [setEquipmentFormData]
  )

  const createTransactionMutation = useMutation({
    mutationFn: async (payload) => {
      showLoader()
      const response = await baseServicePost(
        entities.transactions.create,
        payload
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader()
        resetForm()
        resetItemForm()
        resetEquipmentForm()
        navigate(
          `/almoxarifado/transacoes/${
            isEntrada
              ? 'entrada'
              : isTransferencia
                ? 'transferencia'
                : 'uso-interno'
          }/`
        )
        queryClient.invalidateQueries(['almox_transactions'])
      }
      hideLoader()
    },
    onError: (error) => {
      console.error('onError ', error)

      forEach(error?.data, (error) => {
        showNotification('warning', error)
      })

      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
      hideLoader()
    },
  })

    const handleSubmit = async () => {
    try {
      const cleanedFormData = cleanPayload(formData)

      const cleanedPayload = {
        ...cleanedFormData,
        transaction: {
          ...cleanedFormData.transaction,
          ...(transactionTypeSelect.selectedValue?.value ===
            TransactionEnums.ENTRADA && {
            buyer_id: selectedOrgan?.id,
          }),
        },
        ...(cleanedFormData.items &&
          cleanedFormData.items.length > 0 && {
            items: cleanedFormData.items.map((item) => cleanPayload(item)),
          }),
        ...(cleanedFormData.equipments &&
          cleanedFormData.equipments.length > 0 && {
            equipments: (cleanedFormData.equipments || []).map((equipment) => {
              const clean = cleanPayload(equipment)

              const raw = equipmentsSelect.rawData?.find(
                (e) => e.id === Number(clean.equipment_id)
              )

              return {
                ...clean,
                product_item_id: raw?.product_item_id ?? null,
                product_id: raw?.product_item?.product_id ?? null,
              }
            }),
          }),
      }
      // ⬅️ VALIDAÇÃO PARA ENTRADA COM NOTA FISCAL
      if (
        transactionTypeSelect.selectedValue?.value ===
          TransactionEnums.ENTRADA &&
        docTypeSelect.selectedValue?.value === DocumentTypeEnums['NOTA FISCAL']
      ) {
        const itemsTotal = calculateItemsTotal(cleanedPayload.items || [])
        const informedTotal =
          parseFloat(cleanedPayload.transaction?.amount_total) || 0

        if (Math.abs(itemsTotal - informedTotal) > 0.01) {
          showNotification(
            'error',
            `Divergência: a soma dos itens (R$ ${itemsTotal.toFixed(2)}) não confere com o valor total informado (R$ ${informedTotal.toFixed(2)}). Ajuste os valores antes de salvar.`
          )
          return
        }
      }

      return await createTransactionMutation.mutateAsync(cleanedPayload)
    } catch (error) {
      console.error('Erro ao enviar transação:', {
        status: error.status,
        message: error.message,
      })
    }
  }
  const handleBack = () =>
    navigate(
      `/almoxarifado/transacoes/${
        isEntrada
          ? 'entrada'
          : isTransferencia
            ? 'transferencia'
            : 'uso-interno'
      }/`
    )

  const renderFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getItemOptions}
          getSelectedValue={getItemSelectedValue}
          getSelectMeta={getSelectMetaItem}
        />
      )
    },
    [formErrors, getItemOptions, getItemSelectedValue]
  )

  const renderEquipmentFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getEquipmentItemOptions}
          getSelectedValue={getEquipmentItemSelectedValue}
          getSelectMeta={getSelectMetaEquipment}
        />
      )
    },
    [formErrors, getEquipmentItemOptions, getEquipmentItemSelectedValue]
  )

  const getItemDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split('.').pop()
    const value = item[fieldName]

    if (value && typeof value === 'object' && 'label' in value) {
      return value.label
    }

    return value || ''
  }, [])

  const getEquipmentDisplayValue = getItemDisplayValue

  return (
    <MainLayout>
      <PageHeader
        title="Cadastro de Transação"
        showBackButton={true}
        backUrl={`/almoxarifado/transacoes/${
          isEntrada
            ? 'entrada'
            : isTransferencia
              ? 'transferencia'
              : 'uso-interno'
        }/`}
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={{ ...formData, items: items }}
          onSubmit={handleSubmit}
          textSubmit="Cadastrar Transação"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={false}>
          {() => (
            <>
              <div key={transactionSection.section}>
                <SectionHeader section={transactionSection.section} />
                <SimpleBody
                  fields={transactionFieldsState}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>
              {showProductsSection && (
                <Box
                  key={productsSection.section}
                  sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                  <ItemListSection
                    section={productsSection.section}
                    fields={productsFieldsState}
                    initialData={items}
                    onItemsChange={handleItemsChange}
                    renderFormBody={renderFormBody}
                    getItemDisplayValue={getItemDisplayValue}
                    itemFormData={itemFormData}
                    onItemChange={handleItemChange}
                    itemFormErrors={formErrors}
                    onFormSubmit={handleSaveItem}
                    initializeItemForm={initializeItemForm}
                    isDisabledButton={isDisabledButton.insumo}
                  />
                </Box>
              )}
              {showEquipmentsSection && (
                <Box
                  key={equipmentsSection.section}
                  sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                  <ItemListSection
                    section={equipmentsSection.section}
                    fields={equipmentsFieldsState}
                    initialData={equipments}
                    onItemsChange={handleEquipmentsChange}
                    renderFormBody={renderEquipmentFormBody}
                    getItemDisplayValue={getEquipmentDisplayValue}
                    itemFormData={equipmentFormData}
                    onItemChange={handleEquipmentChange}
                    itemFormErrors={formErrors}
                    onFormSubmit={handleSaveEquipment}
                    initializeItemForm={initializeEquipmentForm}
                    isDisabledButton={isDisabledButton.equipamento}
                  />
                </Box>
              )}
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default CreateTransactionPage
